import React, { useCallback, useId } from 'react';
import clsx from 'clsx';
import { TypedMemo } from '../../../../../common/types';
import { withBrandWord } from '../../../../../music24/components/primitives/BrandWord';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the song page; each part uses its own slice of it */
import styles from '../SongPage.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface SongTextFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: 'text' | 'date';
  /** "optional", next to the label. */
  optionalLabel?: string;
  help?: string;
  error?: string;
  mono?: boolean;
  placeholder?: string;
  maxLength?: number;
  testId?: string;
}

/** One text field of the Details section (design b1, b3): the label, the box, the help or the error. */
const SongTextFieldComponent: React.FC<SongTextFieldProps> = ({
  label,
  value,
  onChange,
  type = 'text',
  optionalLabel,
  help,
  error,
  mono = false,
  placeholder,
  maxLength,
  testId,
}) => {
  const id = useId();
  const change = useCallback((event: React.ChangeEvent<HTMLInputElement>) => onChange(event.target.value), [onChange]);
  return (
    <div className={styles.field}>
      <label className={styles.label} htmlFor={id}>
        {label}
        {optionalLabel !== undefined && <span className={styles.labelOpt}>{optionalLabel}</span>}
      </label>
      <input
        id={id}
        type={type}
        className={clsx(styles.input, mono && styles.inputMono, error !== undefined && styles.inputBad)}
        value={value}
        placeholder={placeholder}
        maxLength={maxLength}
        aria-invalid={error !== undefined}
        onChange={change}
        data-testid={testId}
      />
      {error !== undefined ? (
        <span className={styles.errorText} role="alert">
          {error}
        </span>
      ) : (
        help !== undefined && <span className={styles.help}>{withBrandWord(help)}</span>
      )}
    </div>
  );
};

export const SongTextField = TypedMemo(SongTextFieldComponent);

/** A fact the user cannot change here (design b2, f7): the dashed box. */
export const SongReadOnlyField: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className={styles.field}>
    <span className={styles.label}>{label}</span>
    <span className={styles.readOnly}>{value}</span>
  </div>
);
